import { QueryClient, useQuery } from 'react-query';
import {
  MutationStartThirdPartyAuthArgs,
  MutationUpdateMyNotificationPreferenceArgs,
  MutationUpdateProfileArgs,
  QueryGetThirdPartyAuthByProviderArgs,
  ThirdPartyAuthProvider,
} from '../types/appsync-types';
import { appsyncGraphql } from './appsyncHelper';

fetchMyProfile.id = 'myProfile';

export function fetchMyProfile() {
  const query = /* GraphQL */ `
    query GetMyProfileQuery {
      getMyProfile {
        sub
        phonenumber
        firstname
        lastname
        email
        inviteCode
        registerInviteCode
        creditBalance
        skipDepositAmountInCents
        currency
        language
        organization
        createdAt
        updatedAt
        userRole
      }
    }
  `;
  return appsyncGraphql<'getMyProfile'>(query, {});
}

useMyProfileQuery.id = fetchMyProfile.id;

export function useMyProfileQuery() {
  return useQuery(fetchMyProfile.id, fetchMyProfile);
}
export function syncProfileToPinpoint() {
  return appsyncGraphql<'updateProfilePinpoint'>(/* GraphQL */ `
    mutation UpdateProfilePinpoint {
      updateProfilePinpoint {
        sub
      }
    }
  `);
}

export function getMyThirdPartyAuthByProvider(provider: ThirdPartyAuthProvider) {
  const variables: QueryGetThirdPartyAuthByProviderArgs = { provider };
  const query = /* GraphQL */ `
    query GetThirdPartyAuthByProvider($provider: ThirdPartyAuthProvider!) {
      getThirdPartyAuthByProvider(provider: $provider) {
        userId
        authProvider
        createdAt
      }
    }
  `;
  return appsyncGraphql<'getThirdPartyAuthByProvider'>(query, variables);
}

useGetMyThirdPartyAuthByProvider.id = 'useGetMyThirdPartyAuthByProvider';

export function useGetMyThirdPartyAuthByProvider(provider: ThirdPartyAuthProvider) {
  return useQuery(useGetMyThirdPartyAuthByProvider.id, async () => getMyThirdPartyAuthByProvider(provider));
}

export function getMyThirdPartyAuths() {
  const query = /* GraphQL */ `
    query GetMyThirdPartyAuths {
      getMyThirdPartyAuths {
        userId
        authProvider
      }
    }
  `;
  return appsyncGraphql<'getMyThirdPartyAuths'>(query, {});
}

useGetMyThirdPartyAuths.id = 'useGetMyThirdPartyAuths';

export function useGetMyThirdPartyAuths() {
  return useQuery(useGetMyThirdPartyAuths.id, getMyThirdPartyAuths);
}

export function startThirdPartyAuth(input: MutationStartThirdPartyAuthArgs) {
  const mutation = /* GraphQL */ `
    mutation StartThirdPartyAuth($input: ThirdPartyAuthInput) {
      startThirdPartyAuth(input: $input) {
        authUrl
        state
      }
    }
  `;
  return appsyncGraphql<'startThirdPartyAuth'>(mutation, input);
}

export async function updateProfile(input: MutationUpdateProfileArgs) {
  const mutation = /* GraphQL */ `
    mutation UpdateProfile(
      $phonenumber: AWSPhone
      $firstname: String
      $lastname: String
      $organization: String
      $email: AWSEmail
    ) {
      updateProfile(
        phonenumber: $phonenumber
        firstname: $firstname
        lastname: $lastname
        organization: $organization
        email: $email
      ) {
        sub
        phonenumber
        firstname
        lastname
        organization
        createdAt
        updatedAt
      }
    }
  `;
  await appsyncGraphql<'updateProfile'>(mutation, input);
  /* After profile mutations, we need to start a sync to pinpoint */
  syncProfileToPinpoint();
}

export function updatePreferredLanguage(input: Pick<MutationUpdateProfileArgs, 'language'>) {
  const mutation = /* GraphQL */ `
    mutation UpdateProfile($language: SupportLanguage) {
      updateProfile(language: $language) {
        sub
        phonenumber
        firstname
        lastname
        language
        organization
        createdAt
        updatedAt
      }
    }
  `;
  return appsyncGraphql<'updateProfile'>(mutation, input);
}

fetchMyNotificationPreference.id = 'myNotificationPreference';

export function fetchMyNotificationPreference() {
  const query = /* GraphQL */ `
    query GetMyNotificationPreference {
      getMyNotificationPreference {
        beforeStartingTenMinutes
        beforeStaringOneDay
        startingNow
        beforeEndingTenMinutes
        completed
        news
      }
    }
  `;
  return appsyncGraphql<'getMyNotificationPreference'>(query, {});
}

export function useMyNotificationPreferenceQuery() {
  return useQuery(fetchMyNotificationPreference.id, fetchMyNotificationPreference);
}

export function updateNotificationPreference(input: MutationUpdateMyNotificationPreferenceArgs) {
  const mutation = /* GraphQL */ `
    mutation NotificationPreference($input: NotificationPreferenceInput) {
      updateMyNotificationPreference(input: $input) {
        beforeStartingTenMinutes
        beforeStaringOneDay
        startingNow
        beforeEndingTenMinutes
        completed
        news
      }
    }
  `;
  return appsyncGraphql<'updateMyNotificationPreference'>(mutation, input);
}

export function invalidateThirdPartyAuthData(queryClient: QueryClient) {
  queryClient.invalidateQueries(useGetMyThirdPartyAuthByProvider.id);
  queryClient.invalidateQueries(useGetMyThirdPartyAuths.id);
}
